/**
 * RepeatableDiscriminatorDialog — UX nudge al marcar repeatable en un
 * fixed_heading que no cumple §3.3.1.
 *
 * Doc 06 §3.3.1: un fixed_heading repetible debe tener primer hijo
 * `plain_text required` (discriminador — identifica cada instancia).
 *
 * Backend: la regla vive en publish-gate (no en schema base). El draft
 * acepta repeatable=true sin discriminador durante el armado. Este
 * dialog ofrece dos caminos al user:
 *
 *   · "Crear identificador" → atómico: marca repeatable + inserta/
 *     promueve el discriminador. Vía `ENSURE_REPEATABLE_DISCRIMINATOR`.
 *   · "Lo agrego yo" → solo marca repeatable. El user es responsable
 *     de agregar el discriminador antes de publicar (publish-gate lo
 *     enforza).
 */

import { useTranslation } from 'react-i18next'

import { Dialog } from '../../Dialog'

interface Props {
  open: boolean
  /** Título de la sección (en el locale activo) para mostrar en el body. */
  sectionTitle: string
  /** Caso del container que dispara el dialog. Afecta el copy:
   *  - 'empty'        → no tiene hijos
   *  - 'wrong_first'  → tiene hijos pero el primero no es plain_text required
   */
  reason: 'empty' | 'wrong_first'
  onConfirmAutoCreate: () => void
  onConfirmManual: () => void
  onCancel: () => void
}

export function RepeatableDiscriminatorDialog({
  open,
  sectionTitle,
  reason,
  onConfirmAutoCreate,
  onConfirmManual,
  onCancel,
}: Props) {
  const { t } = useTranslation('templates')

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t('designer.editor.repeatable_discriminator_title')}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-ui text-ink-muted hover:text-ink"
          >
            {t('designer.editor.repeatable_discriminator_cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirmManual}
            className="px-3 py-1.5 text-sm font-ui rounded-md border border-line text-ink hover:bg-surface-muted"
          >
            {t('designer.editor.repeatable_discriminator_manual')}
          </button>
          <button
            type="button"
            onClick={onConfirmAutoCreate}
            className="px-3 py-1.5 text-sm font-ui rounded-md bg-brand text-canvas hover:bg-brand-deep"
            autoFocus
          >
            {t('designer.editor.repeatable_discriminator_auto')}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-ink font-body leading-relaxed">
        <p>
          {t('designer.editor.repeatable_discriminator_body_intro', {
            section: sectionTitle,
          })}
        </p>
        <p className="text-ink-muted text-xs">
          {reason === 'empty'
            ? t('designer.editor.repeatable_discriminator_body_empty')
            : t('designer.editor.repeatable_discriminator_body_wrong_first')}
        </p>
        <div className="rounded-md border border-brand/30 bg-brand-soft/30 p-3 text-xs text-ink">
          <p className="font-semibold mb-1">
            {t('designer.editor.repeatable_discriminator_options_title')}
          </p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>
              <strong>{t('designer.editor.repeatable_discriminator_auto')}</strong>:{' '}
              {t('designer.editor.repeatable_discriminator_option_auto_help')}
            </li>
            <li>
              <strong>
                {t('designer.editor.repeatable_discriminator_manual')}
              </strong>
              : {t('designer.editor.repeatable_discriminator_option_manual_help')}
            </li>
          </ul>
        </div>
      </div>
    </Dialog>
  )
}
